import {
  switchMap,
  catchError,
  mergeMap,
  withLatestFrom,
} from 'rxjs/operators';
import {
  Observable,
  of,
  from,
  forkJoin,
} from 'rxjs';
import { ofType } from 'redux-observable';
import { UserInfo } from '../../shared/models/user';
import UserService from '../services/user.service';
import {
  CreateUser,
  CreateUserFailed,
  CreateUserSuccess,
  LoadUserForUpdate,
  LoadUserForUpdateSuccess,
  LoadUserInfo,
  LoadUserInfoFailed,
  LoadUserInfoSucceed,
  LoadUsers,
  LoadUsersFailed,
  LoadUsersSucceed,
  RemoveUsers,
  RemoveUsersFailed,
  RemoveUsersSucceed,
  UserManagementAction,
  UserManagementActions,
  LoadUserForUpdateFailed,
  UpdateUser,
  UpdateUserSuccess,
  UpdateUserFailed,
  ResetUserPassword,
  ResetUserPasswordSuccess,
  ReInviteUserFailed,
  ReInviteUser,
  ReInviteUserSuccess,
  MigrateUser, MigrateUserSuccess, MigrateUserFailed,
} from '../actions/userManagement.action';
import OrgService from '../../auth/services/org.service';
import { RootState } from '../../store/state';
import { CreateErrorNotice, CreateSuccessNotice } from '../../store/actions/toaster.action';
import { ServerErrorMessages } from '../../shared/utils/server-error-utils';

const getUserEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<LoadUserInfo>(UserManagementActions.LoadUserInfo),
  switchMap((action) => from(UserService.getUserInfo(action.payload)).pipe(
    mergeMap((data: UserInfo) => of(
      new LoadUserInfoSucceed(data),
    )),
    catchError((error: Error) => of(
      new LoadUserInfoFailed(error.message || 'Unknown user loading error!'),
      new CreateErrorNotice('Error', ServerErrorMessages[error.message] ||  'Failed to load users data.')
    )),
  )),
);

const getUsersEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<LoadUsers>(UserManagementActions.LoadUsers),
  switchMap((action) => from(OrgService.getOrgUsers(action.payload.orgId, action.payload.includeChildren)).pipe(
    mergeMap((data: UserInfo[]) => of(
      new LoadUsersSucceed(data),
    )),
    catchError((error: Error) => of(
      new LoadUsersFailed(error.message || 'Unknown users loading error!'),
      new CreateErrorNotice('Error', ServerErrorMessages[error.message] || 'Failed to load user data.')
    )),
  )),
);

const removeUsersEpic = ($action: Observable<UserManagementAction>, $state: Observable<RootState>) => $action.pipe(
  ofType<RemoveUsers>(UserManagementActions.RemoveUsers),
  withLatestFrom($state),
  switchMap(([action, state]) => forkJoin(
    action.payload?.ids?.map((userId) => UserService.removeUser(userId)),
  ).pipe(
    mergeMap(() => of(
      new RemoveUsersSucceed(),
      new LoadUsers({
        orgId: state?.orgHierarchy?.selectedOrg?._id,
        includeChildren: action.payload.includeChildren
      }),
      new CreateSuccessNotice('Success', 'Users were removed successfully!'),
    )),
    catchError((error: Error) => of(
      new RemoveUsersFailed(error.message || 'Unknown agent loading error!'),
      new CreateErrorNotice('Error', ServerErrorMessages[error.message] || 'Failed to remove users.')
    )),
  )),
);


const createUserEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<CreateUser>(UserManagementActions.CreateUser),
  switchMap((action) => from(UserService.createUser(action.payload.orgId, action.payload.body, action.payload.includeChildren)).pipe(
    mergeMap((user) => of(
      new CreateUserSuccess(user),
      new LoadUsers({
        orgId: action.payload.orgId,
        includeChildren: action.payload.includeChildren
      }),
      new CreateSuccessNotice('Success', 'User was created successfully!'),
    )),
    catchError((error: Error) => of(
      new CreateUserFailed(error.message || 'Unknown users loading error!'),
      new CreateErrorNotice('Error', ServerErrorMessages[error.message] || 'Failed to create a user.')
    )),
  )),
);

const loadUserForUpdateEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<LoadUserForUpdate>(UserManagementActions.LoadUserForUpdate),
  switchMap((action) => from(UserService.getUserInfo(action.payload)).pipe(
    mergeMap((user) => of(
      new LoadUserForUpdateSuccess(user),
    )),
    catchError((error: Error) => of(new LoadUserForUpdateFailed(error.message || 'Couldn\'t load user'))),
  )),
);

const updateUserEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<UpdateUser>(UserManagementActions.UpdateUser),
  switchMap((action) => from(UserService.updateProfileUser(action.payload.userId, action.payload.body)).pipe(
    mergeMap((user) => of(
      new UpdateUserSuccess(user),
      new CreateSuccessNotice('Success', 'User information updated'),
    )),
    catchError((error: Error) => of(
      new UpdateUserFailed(error.message || 'Failed to update user'),
      new CreateErrorNotice(
        'Error',
        ServerErrorMessages[error.message] || 'Failed to update user'
      ),
      ),
    )),
  ));

const reInviteUserEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<ReInviteUser>(UserManagementActions.ReInviteUser),
  switchMap(({ userId }) => from(UserService.reInviteUser(userId)).pipe(
    mergeMap(() => of(
      new ReInviteUserSuccess(),
      new CreateSuccessNotice('Success', 'User was re-invited successfully!'),
    ))
  )),
  catchError((error: Error) => of(
    new ReInviteUserFailed(error.message || 'Unknown user reinvite error!'),
    new CreateErrorNotice('Error', ServerErrorMessages[error.message] || 'Failed to reinvite user.'),
  )),
);

const resetUserPasswordEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<ResetUserPassword>(UserManagementActions.ResetUserPassword),
  switchMap((action) => from(UserService.updateUserPassword(action.payload.userId, action.payload.body)).pipe(
    mergeMap((user) => of(
      new ResetUserPasswordSuccess(user),
      new CreateSuccessNotice('Success', 'Users password updated'),
    )),
    catchError((error: Error) => of(
      new UpdateUserFailed(error.message || 'Failed to update users password'),
      new CreateErrorNotice(
        'Error',
        ServerErrorMessages[error.message] || 'Failed to update user'
      ),
      ),
    )),
  ));

const migrateUsersEpic = ($action: Observable<UserManagementAction>) => $action.pipe(
  ofType<MigrateUser>(UserManagementActions.MigrateUser),
  switchMap((action) => from(UserService.migrateUser(action.payload.userId, action.payload.orgId)).pipe(
    mergeMap((data: any) => of(
      new MigrateUserSuccess(data),
    )),
    catchError((error: Error) => of(
      new MigrateUserFailed(error.message || 'Unable to migrate a user!'),
      new CreateErrorNotice('Error', ServerErrorMessages[error.message] || 'Failed to migrate a user.')
    )),
  )),
);



const UserManagementEpics = [
  getUserEpic,
  getUsersEpic,
  removeUsersEpic,
  createUserEpic,
  reInviteUserEpic,
  loadUserForUpdateEpic,
  updateUserEpic,
  resetUserPasswordEpic,
  migrateUsersEpic
];

export default UserManagementEpics;
